/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isInteger from '@stdlib/math/base/assert/is-integer';
import pow from '@stdlib/math/base/special/pow';


// MAIN //

/**
* Returns the excess kurtosis of a discrete uniform distribution.
*
* @param {integer} a - minimum support
* @param {integer} b - maximum support
* @returns {number} excess kurtosis
*
* @example
* var v = kurtosis( 0, 1 );
* // returns -2.0
*
* @example
* var v = kurtosis( 4, 12 );
* // returns ~-1.23
*
* @example
* var v = kurtosis( -4, 4 );
* // returns ~-1.23
*
* @example
* var v = kurtosis( 1, 0.4 );
* // returns NaN
*
* @example
* var v = kurtosis( -0.1, 1 );
* // returns NaN
*
* @example
* var v = kurtosis( 2, NaN );
* // returns NaN
*
* @example
* var v = kurtosis( NaN, 2 );
* // returns NaN
*/
function kurtosis( a, b ) {
	var n2;
	if (
		!isInteger( a ) ||
		!isInteger( b ) ||
		a > b
	) {
		return NaN;
	}
	n2 = pow( b - a + 1.0, 2.0 );
	return -1.2 * ( n2 + 1.0 ) / ( n2 - 1.0 );
}


// EXPORTS //

export default kurtosis;
